import React, { useState } from 'react';
import { colors } from '../styles/colors';

interface UnpublishModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, recruiterId: number, createAfterSales: boolean) => Promise<void>;
  opportunityName: string;
  recruiters: Array<{ id: number; name: string; email: string; recruiter_id: number }>;
}

const UnpublishModal: React.FC<UnpublishModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  opportunityName,
  recruiters
}) => {
  const [reason, setReason] = useState('');
  const [recruiterId, setRecruiterId] = useState<number | ''>('');
  const [createAfterSales, setCreateAfterSales] = useState(true);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (reason.trim() && recruiterId) {
      try {
        setLoading(true);
        await onConfirm(reason.trim(), Number(recruiterId), createAfterSales);
        onClose();
        setReason('');
        setRecruiterId('');
        setCreateAfterSales(true);
      } catch (error) {
        // Error is handled by the parent component
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setReason('');
      setRecruiterId('');
      setCreateAfterSales(true);
    }
  };

  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={styles.iconContainer}>
            <span style={styles.icon}>⚠️</span>
          </div>
          <h2 style={styles.title}>Despublicar Vaga</h2>
        </div>
        
        <div style={styles.content}>
          <div style={styles.warningBox}>
            <strong>ATENÇÃO: Esta ação irá despublicar a vaga!</strong>
          </div>
          
          <p style={styles.message}>
            Tem certeza que deseja <strong>despublicar</strong> a vaga:
          </p>
          <div style={styles.opportunityName}>
            "{opportunityName}"
          </div>
          
          <div style={styles.consequencesList}>
            <p style={styles.consequencesTitle}>Consequências desta ação:</p>
            <ul style={styles.consequences}>
              <li>A vaga será <strong>despublicada</strong> (sairá do ar)</li>
              <li>A vaga será <strong>expirada</strong> imediatamente</li>
              <li>Será criado um log na atividade da empresa</li>
              <li>Uma pendência de pós-venda será criada (se não marcado para pular)</li>
            </ul>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Motivo da despublicação *</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={styles.textarea}
              rows={4}
              placeholder="Descreva o motivo da despublicação..."
              required
              disabled={loading}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Recrutador responsável *</label>
            <select
              value={recruiterId}
              onChange={(e) => setRecruiterId(e.target.value ? Number(e.target.value) : '')}
              style={styles.select}
              required
              disabled={loading}
            >
              <option value="">Selecione um recrutador</option>
              {recruiters.map(recruiter => (
                <option key={recruiter.recruiter_id} value={recruiter.recruiter_id}>
                  {recruiter.name} ({recruiter.email})
                </option>
              ))}
            </select>
          </div>

          <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={createAfterSales}
                onChange={(e) => setCreateAfterSales(e.target.checked)}
                style={styles.checkbox}
                disabled={loading}
              />
              Criar uma pendência de Pós venda: Oportunidade Expirada
            </label>
          </div>
          
          {loading && (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner}></div>
              <span style={styles.loadingText}>Despublicando vaga...</span>
            </div>
          )}
        </div>
        
        <div style={styles.actions}>
          <button 
            onClick={handleClose} 
            style={{
              ...styles.cancelBtn,
              ...(loading ? styles.disabledBtn : {})
            }}
            disabled={loading}
          >
            Cancelar
          </button>
          <button 
            onClick={handleConfirm} 
            style={{
              ...styles.dangerBtn,
              ...(loading ? styles.disabledBtn : {})
            }}
            disabled={loading || !reason.trim() || !recruiterId}
          >
            {loading ? 'Despublicando...' : 'Despublicar Vaga'}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    width: '95%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
  },
  header: {
    padding: '2rem 2rem 1rem 2rem',
    textAlign: 'center',
    backgroundColor: '#FEF2F2',
  },
  iconContainer: {
    marginBottom: '1rem',
  },
  icon: {
    fontSize: '3rem',
    display: 'block',
  },
  title: {
    margin: 0,
    color: colors.error,
    fontSize: '1.5rem',
    fontWeight: 'bold',
  },
  content: {
    padding: '1.5rem 2rem',
  },
  warningBox: {
    padding: '1rem',
    backgroundColor: '#FEF2F2',
    border: `2px solid ${colors.error}`,
    borderRadius: '8px',
    color: colors.error,
    textAlign: 'center',
    fontSize: '1rem',
    fontWeight: 'bold',
    marginBottom: '1.5rem',
  },
  message: {
    margin: '0 0 1rem 0',
    color: colors.text,
    fontSize: '1rem',
    lineHeight: '1.5',
    textAlign: 'center',
  },
  opportunityName: {
    padding: '1rem',
    backgroundColor: colors.background,
    borderRadius: '8px',
    border: `2px solid ${colors.error}`,
    color: colors.error,
    fontWeight: 'bold',
    fontSize: '1.1rem',
    margin: '1rem 0 1.5rem 0',
    textAlign: 'center',
  },
  consequencesList: {
    marginBottom: '1.5rem',
  },
  consequencesTitle: {
    margin: '0 0 0.5rem 0',
    color: colors.text,
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  consequences: {
    margin: '0 0 1.5rem 0',
    paddingLeft: '1.5rem',
    color: colors.textLight,
    fontSize: '0.9rem',
    lineHeight: '1.6',
  },
  formGroup: {
    marginBottom: '1.5rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 'bold',
    color: colors.text,
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '100px',
  },
  select: {
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    fontFamily: 'inherit',
    backgroundColor: colors.white,
  },
  checkboxGroup: {
    marginBottom: '1rem',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: colors.text,
    cursor: 'pointer',
  },
  checkbox: {
    width: '1.2rem',
    height: '1.2rem',
  },
  actions: {
    display: 'flex',
    gap: '1rem',
    padding: '1.5rem 2rem 2rem 2rem',
    justifyContent: 'center',
  },
  cancelBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.white,
    color: colors.textLight,
    border: `2px solid ${colors.border}`,
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontFamily: 'inherit',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  dangerBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.error,
    color: colors.white,
    border: `2px solid ${colors.error}`,
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontFamily: 'inherit',
    fontWeight: 'bold',
    transition: 'all 0.2s ease',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: colors.background,
    borderRadius: '4px',
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: `2px solid ${colors.border}`,
    borderTop: `2px solid ${colors.error}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    fontSize: '0.9rem',
    color: colors.textLight,
  },
  disabledBtn: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
};

export default UnpublishModal;